import {
  HEARTBEAT_INTERVAL_MS,
  LIVENESS_TIMEOUT_MS,
  RECONNECT_BASE_DELAY_MS,
  RECONNECT_JITTER,
  RECONNECT_MAX_DELAY_MS,
  decodeServerMessage,
  encodeClientMessage,
  isFatalError,
  type ClientMessage,
  type ErrorCode,
  type ServerMessage,
} from '@sokdaq/protocol';

export type ConnectionStatus =
  /** 처음 붙는 중. */
  | 'connecting'
  /** welcome까지 받아 정상 동작 중. */
  | 'open'
  /** 끊겼고 백오프 후 다시 시도할 예정. */
  | 'reconnecting'
  /** 사용자가 나갔다. */
  | 'closed'
  /** 재시도해도 소용없는 상태(강퇴, 방 없음, 버전 불일치). */
  | 'failed';

export interface ConnectionOptions {
  /**
   * 접속할 때마다 새로 묻는다.
   *
   * 고정 문자열이 아닌 이유는 초대코드 회전이다. 방장이 코드를 갈아치우면
   * URL의 코드도 바뀌는데, 처음 값을 붙들고 있으면 다음 재접속이 이미
   * 죽은 코드로 가서 404로 실패한다.
   */
  readonly url: () => string;
  /**
   * 접속할 때마다 새로 만든다.
   *
   * 매번 호출하는 게 핵심이다. 재접속 시점의 최신 memberToken과 sinceSeq가
   * 실려야 놓친 구간만 받아올 수 있다.
   */
  readonly makeHello: () => Extract<ClientMessage, { t: 'hello' }>;
  readonly onMessage: (message: ServerMessage) => void;
  readonly onStatus: (status: ConnectionStatus, detail?: string) => void;
}

/**
 * 이 프레임들은 오프라인 큐에 넣지 않는다.
 *
 * 좌표나 타이핑 상태는 재연결될 무렵이면 이미 낡았다. 밀린 것을 몰아
 * 보내면 아바타가 과거 경로를 되짚으며 순간이동한다.
 */
const EPHEMERAL: ReadonlySet<ClientMessage['t']> = new Set(['move', 'typing', 'ping']);

/**
 * 방과의 WebSocket 연결.
 *
 * 재연결·하트비트·오프라인 큐를 여기서 전부 처리하고, 바깥에는
 * "메시지가 왔다"와 "상태가 변했다"만 알린다.
 */
export class RoomConnection {
  private socket: WebSocket | null = null;
  private attempt = 0;
  /** welcome을 받기 전에는 hello 외의 프레임을 보낼 수 없다. */
  private ready = false;
  private disposed = false;
  private readonly outbox: ClientMessage[] = [];
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** 서버에서 마지막으로 뭔가 받은 시각. 죽은 연결 판정에 쓴다. */
  private lastFrameAt = 0;
  private detachWakeListeners: (() => void) | null = null;

  constructor(private readonly options: ConnectionOptions) {
    this.attachWakeListeners();
  }

  connect(): void {
    if (this.disposed) return;

    this.clearTimers();
    this.ready = false;
    this.options.onStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    let socket: WebSocket;
    try {
      socket = new WebSocket(this.options.url());
    } catch (error) {
      this.scheduleReconnect(error instanceof Error ? error.message : '연결할 수 없습니다');
      return;
    }
    this.socket = socket;

    socket.addEventListener('open', () => {
      socket.send(encodeClientMessage(this.options.makeHello()));
    });

    socket.addEventListener('message', (event) => {
      this.handleFrame(String(event.data));
    });

    socket.addEventListener('close', (event) => {
      if (this.socket === socket) this.socket = null;
      this.stopHeartbeat();
      // 서버가 정책 위반으로 끊은 경우(강퇴 등)는 error 프레임에서
      // 이미 처리됐다. disposed면 사용자가 나간 것이다.
      if (!this.disposed) this.scheduleReconnect(closeReason(event));
    });

    socket.addEventListener('error', () => {
      // close가 뒤따라 오므로 여기서는 재연결을 걸지 않는다.
      // 두 번 걸면 백오프가 반토막 난다.
    });
  }

  /**
   * 프레임을 보낸다. 연결이 없으면 큐에 넣고 재접속 후 순서대로 보낸다.
   * 일시적 상태(이동·타이핑)는 큐에 넣지 않고 버린다.
   */
  send(message: ClientMessage): void {
    if (this.ready && this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(encodeClientMessage(message));
      return;
    }
    if (!EPHEMERAL.has(message.t)) this.outbox.push(message);
  }

  /** 사용자가 방을 떠난다. 재연결하지 않는다. */
  dispose(): void {
    this.teardown(1000, 'left');
    this.options.onStatus('closed');
  }

  private handleFrame(raw: string): void {
    this.lastFrameAt = Date.now();

    const decoded = decodeServerMessage(raw);
    if (!decoded.ok) {
      // 서버가 보낸 프레임을 못 읽는 건 버전 불일치일 가능성이 높다.
      // 연결을 끊지는 않는다 — 나머지 프레임은 정상일 수 있다.
      console.warn('알 수 없는 서버 프레임:', decoded.reason);
      return;
    }
    const message = decoded.value;

    if (message.t === 'welcome') {
      this.onReady();
    } else if (message.t === 'error' && isFatalError(message.code as ErrorCode)) {
      this.fail(message.message);
      this.options.onMessage(message);
      return;
    }

    this.options.onMessage(message);
  }

  private onReady(): void {
    this.ready = true;
    this.attempt = 0;
    this.options.onStatus('open');
    this.startHeartbeat();
    this.flushOutbox();
  }

  private flushOutbox(): void {
    // 순서를 지켜서 보낸다. 서버는 메시지 id로 중복을 걸러내므로
    // 재연결 직전에 이미 도착한 것이 섞여 있어도 안전하다.
    while (this.outbox.length > 0 && this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(encodeClientMessage(this.outbox.shift()!));
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.lastFrameAt = Date.now();

    this.heartbeatTimer = setInterval(() => {
      if (this.socket?.readyState !== WebSocket.OPEN) return;

      // 서버가 조용한 지 너무 오래됐으면 half-open으로 본다.
      // close 이벤트를 기다려 봐야 오지 않는 상태다.
      if (Date.now() - this.lastFrameAt > LIVENESS_TIMEOUT_MS) {
        this.socket.close(4000, 'no response');
        return;
      }

      this.socket.send(encodeClientMessage({ t: 'ping' }));
    }, HEARTBEAT_INTERVAL_MS);
  }

  /**
   * 절전에서 깨거나 네트워크가 돌아오면 백오프를 기다리지 않고 바로 붙는다.
   *
   * 노트북을 열자마자 30초를 기다리게 하면 켜둔 의미가 없다.
   */
  private attachWakeListeners(): void {
    const retryNow = () => {
      if (this.disposed || this.ready) return;
      if (this.reconnectTimer === null) return;

      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
      this.connect();
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') retryNow();
    };

    window.addEventListener('online', retryNow);
    document.addEventListener('visibilitychange', onVisible);

    this.detachWakeListeners = () => {
      window.removeEventListener('online', retryNow);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  private scheduleReconnect(detail: string): void {
    if (this.disposed) return;

    const delay = backoffDelay(this.attempt);
    this.attempt += 1;
    this.options.onStatus('reconnecting', detail);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  /** 재시도해도 소용없는 상태. 백오프를 멈춘다. */
  private fail(detail: string): void {
    this.teardown(1000, 'fatal');
    this.options.onStatus('failed', detail);
  }

  private teardown(code: number, reason: string): void {
    this.disposed = true;
    this.ready = false;
    this.clearTimers();
    this.detachWakeListeners?.();
    this.detachWakeListeners = null;
    this.socket?.close(code, reason);
    this.socket = null;
  }

  private clearTimers(): void {
    this.stopHeartbeat();
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }
}

/**
 * 지수 백오프 + 지터.
 *
 * 지터가 없으면 서버가 잠깐 죽었다 살아날 때 모든 클라이언트가 같은
 * 순간에 재접속해 다시 쓰러뜨린다.
 */
export function backoffDelay(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** attempt, RECONNECT_MAX_DELAY_MS);
  return Math.round(base * (1 + (random() * 2 - 1) * RECONNECT_JITTER));
}

function closeReason(event: CloseEvent): string {
  if (event.reason) return event.reason;
  return event.wasClean ? '연결이 종료되었습니다' : '연결이 끊겼습니다';
}
